import React, { useEffect, useState } from "react";
import { Box, Button, Grid, TextField } from "@mui/material";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../services/utils";
import { addRoleSchema, editRoleSchema } from "../../schemas/Role.schema";
import { createRole, updateRole } from "../../services/roleService";
import useFetchRoleById from "../../hooks/useFetchRoleById";
import FlexBetween from "../../components/FlexBetween";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ViewJsonSplitButton from "../../components/common/ViewJsonSplitButton";
import Loader from "../../components/Molecules/Loader/Loader";
import { getRoleType } from "../../types/role";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import { validateConfigRequest } from "../../services/resourcesService";
import { useAuth } from "../../contexts/AuthContext";
import { useAppTranslation } from "../../hooks/useAppTranslation";

const AddEditRole: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const { isAdmin } = useAuth();
  const { t } = useAppTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState<any>({});
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] = useState(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);

  // Fetch role data if editing
  const [roleData] = useFetchRoleById({
    setIsLoading,
    roleId: isEditMode && id ? id : null,
  });

  const formik = useFormik({
    initialValues: {
      role_name: "",
      role_description: "",
    },
    validationSchema: isEditMode ? editRoleSchema : addRoleSchema,
    enableReinitialize: true,
    onSubmit: async (payload) => {
      try {
        setIsLoading(true);

        if (isEditMode) {
          // Update existing role - preserve existing role_type or fallback to "user"
          const roleTypeToSend = getRoleType(roleData);
          await updateRole(id!, { ...payload, role_type: roleTypeToSend });
          showToast(t("roles.toast.updated"), "success");
        } else {
          // Create new role - hardcode role_type as "user"
          await createRole({ ...payload, role_type: "user" });
          showToast(t("roles.toast.created"), "success");
        }

        navigate("/roles");
      } catch (error: any) {
        console.error("Failed to save role:", error);
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Populate form with role data when editing
  useEffect(() => {
    if (isEditMode && roleData) {
      const values = {
        role_name: roleData?.role_name || "",
        role_description: roleData?.role_description || "",
      };
      formik.setValues(values);
      setInitialValues(values);
    }
  }, [roleData, isEditMode]);

  // Track form changes
  useEffect(() => {
    // Only check for changes if initialFormData is properly set
    if (Object.keys(formik.values).length > 0) {
      const hasFormChanges =
        JSON.stringify(formik.values) !== JSON.stringify(initialValues);
      setHasChanges(hasFormChanges);
    } else {
      // If initialFormData is not set yet, disable save button
      setHasChanges(false);
    }
  }, [formik.values, initialValues]);

  const buildPayload = () => {
    const roleTypeToSend = isEditMode ? getRoleType(roleData) : "user";
    return { ...formik.values, role_type: roleTypeToSend };
  };

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const validateReqBody = {
        target_endpoint: isEditMode ? `/user-role/${id}` : "/user-role",
        method: isEditMode ? "PUT" : "POST",
        body: payload,
        query_params: {},
        url_params: isEditMode ? { id } : {},
      };
      const response = await validateConfigRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) {
        showToast(t("roles.toast.payloadValid"), "success");
      }
    } catch (error: any) {
      showToast(
        error?.response?.data?.message || t("roles.toast.validationFailed"),
        "error"
      );
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopyPayload = async () => {
    const payload = buildPayload();
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    showToast(t("roles.toast.payloadCopied"), "success");
  };

  return (
    <Box>
      <Loader isLoading={isLoading} />
      <form onSubmit={formik.handleSubmit} autoComplete="off">
        <Box className="text-box-card compact-text-box-card">
          <Grid container rowSpacing={1.5} columnSpacing={2.5}>
            <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
              <label className="label-text">
                {t("roles.form.roleName")}
                <span className="required-asterisk">*</span>
              </label>
              <TextField
                type="text"
                name="role_name"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`form-control ${
                  formik.touched.role_name && formik.errors.role_name
                    ? "has-error"
                    : ""
                }`}
                error={!!(formik.touched.role_name && formik.errors.role_name)}
                helperText={formik.touched.role_name && formik.errors.role_name}
                value={formik.values.role_name}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
              <label className="label-text">
                {t("roles.form.description")}
              </label>
              <TextField
                type="text"
                name="role_description"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`form-control ${
                  formik.touched.role_description &&
                  formik.errors.role_description
                    ? "has-error"
                    : ""
                }`}
                error={
                  !!(
                    formik.touched.role_description &&
                    formik.errors.role_description
                  )
                }
                helperText={
                  formik.touched.role_description &&
                  formik.errors.role_description
                }
                value={formik.values.role_description}
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <FlexBetween
            gap="3rem"
            sx={{ marginTop: 4, display: "flex", justifyContent: "flex-end" }}
          >
            {/* <Button
              type="button"
              color="secondary"
              variant="contained"
              onClick={() => navigate("/roles")}
              className="btn-orange btn-dark"
            >
              Cancel
            </Button> */}
            <ViewJsonSplitButton
              onViewJson={() => setIsPayloadDialogOpen(true)}
              onCopyJson={handleCopyPayload}
            />
            <Button
              type="submit"
              className="btn-orange"
              color="secondary"
              variant="contained"
              disabled={isEditMode ? !hasChanges : false}
            >
              <SaveOutlinedIcon /> &nbsp;{" "}
              {isEditMode ? t("roles.form.update") : t("roles.form.save")}
            </Button>
          </FlexBetween>
        </Box>
      </form>

      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        dialogTitle={t("roles.form.jsonDialogTitle")}
        payload={buildPayload()}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </Box>
  );
};

export default AddEditRole;
